import { useState } from "react";

import { useDrag } from "react-dnd";

import {
  BoltIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyPoundIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import useNotification from "../notifications/useNotifications";
import Tooltip from "../shared/Tooltip";
import Avatar from "../user/Avatar";
import useApiHelper from "../../hooks/useApiHelper";
import { useSignalR } from "../../contexts/SignalRContext";
import { DragTypes } from "../../models/DragTypes";
import { IAllJobsResponse } from "../../models/Job";
import {
  IPlannerDragItem,
  IPlannerVehicleRow,
  PlannerColourBy,
} from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import {
  ageBorderClasses,
  complexityBorderClasses,
  complexityChipClasses,
  complexityLabel,
  daysOnSite,
  lineChipClasses,
  lineLabel,
} from "../../utils/vehicleDisplay";

interface PlannerVehicleCardProps {
  vehicle: IPlannerVehicleRow;
  colourBy: PlannerColourBy;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

export default function PlannerVehicleCard({
  vehicle,
  colourBy,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: PlannerVehicleCardProps) {
  const { get } = useApiHelper();
  const { addNotification } = useNotification();
  const { activeJobSessionsByVehicle } = useSignalR();
  const [isOpeningJob, setIsOpeningJob] = useState(false);

  const activeJobUsers = activeJobSessionsByVehicle[vehicle.id] ?? [];

  const days = daysOnSite(vehicle.arrivalDate, vehicle.collectedOn);

  const fuel = (vehicle.fuelType ?? "").toLowerCase();
  const isHybrid = fuel.includes("hybrid");
  const isElectric = !isHybrid && (fuel.includes("electric") || fuel.includes("electricity"));
  const isHighValue = (vehicle.partsListValue ?? 0) > 1000;
  const isConvertible = (vehicle.bodyStyle ?? "").toLowerCase().includes("convertible");
  const isFourByFour = vehicle.driveType === "4x4";
  const showHazard = isConvertible || isFourByFour;
  const hazardLabel = isConvertible && isFourByFour
    ? "Convertible & 4x4"
    : isConvertible
      ? "Convertible"
      : "4x4";

  const dragItem: IPlannerDragItem = {
    vehicleId: vehicle.id,
    currentBayId: vehicle.dismantlingBayId,
    currentPlannedOn: vehicle.plannedDismantlingOn,
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragTypes.PLANNER_VEHICLE,
      item: dragItem,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [vehicle.id, vehicle.dismantlingBayId, vehicle.plannedDismantlingOn]
  );

  const handleOpenVehicle = (e: React.MouseEvent) => {
    e.stopPropagation();
    openInNewTab(`/vehicles/${vehicle.id}`);
  };

  const handleOpenJob = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpeningJob) return;

    setIsOpeningJob(true);
    try {
      const response = await get<IAllJobsResponse>(`/job/vehicle/${vehicle.id}`);
      const jobs = response.data ?? [];
      const latest = [...jobs].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      )[0];

      if (!latest) {
        addNotification({
          variant: "warn",
          primaryText: "No job found for this vehicle",
        });
        return;
      }

      openInNewTab(`/jobs/${latest.id}`);
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Unable to load job for this vehicle",
      });
    } finally {
      setIsOpeningJob(false);
    }
  };

  const borderClass =
    colourBy === "Age"
      ? ageBorderClasses(days)
      : complexityBorderClasses(vehicle.complexity);

  const iconButtonClasses =
    "inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50";

  return (
    <div
      ref={drag}
      className={classNames(
        "bg-white rounded-md border border-gray-200 shadow-sm px-2.5 py-2 cursor-grab select-none",
        borderClass,
        isDragging ? "opacity-40" : "hover:shadow"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-1.5">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {vehicle.vrm || "No VRM"}
            </span>
            {isElectric && (
              <Tooltip content="Electric">
                <BoltIcon className="h-3.5 w-3.5 text-yellow-500" />
              </Tooltip>
            )}
            {isHybrid && (
              <Tooltip content="Hybrid">
                <BoltIcon className="h-3.5 w-3.5 text-green-600" />
              </Tooltip>
            )}
            {isHighValue && (
              <Tooltip content={`Parts list worth £${Math.round(vehicle.partsListValue).toLocaleString()}`}>
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-yellow-500 text-yellow-600">
                  <CurrencyPoundIcon className="h-3 w-3" />
                </span>
              </Tooltip>
            )}
            {showHazard && (
              <Tooltip content={hazardLabel}>
                <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-600" />
              </Tooltip>
            )}
          </div>
          <div className="text-xs text-gray-700 truncate">
            {[vehicle.combinedMake, vehicle.combinedModel].filter(Boolean).join(" ")}
            {vehicle.bodyStyle && (
              <span className="ml-1 text-gray-500">{vehicle.bodyStyle}</span>
            )}
          </div>
          <div className="text-[11px] text-gray-500">
            {vehicle.partsRemoved}/{vehicle.partsTotal}{" "}
            {vehicle.partsTotal === 1 ? "part" : "parts"}
          </div>
        </div>
        <div className="flex flex-col items-end gap-y-1 shrink-0">
          <span
            className={classNames(
              "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5",
              complexityChipClasses(vehicle.complexity)
            )}
          >
            {complexityLabel(vehicle.complexity)}
          </span>
          <div className="flex items-center gap-x-1">
            <button
              type="button"
              onClick={handleOpenVehicle}
              className={iconButtonClasses}
              title="Open vehicle"
            >
              <TruckIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={handleOpenJob}
              disabled={isOpeningJob}
              className={iconButtonClasses}
              title="Open job"
            >
              <BriefcaseIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-x-1.5">
        <span
          className={classNames(
            "text-[10px] font-medium rounded px-1.5 py-0.5",
            lineChipClasses(vehicle.productionLine)
          )}
        >
          {lineLabel(vehicle.productionLine)}
        </span>
      </div>

      {vehicle.locationName && (
        <div className="mt-1 text-[11px] text-gray-500 truncate">
          Location: {vehicle.locationName}
        </div>
      )}

      {days !== null && (
        <div className="mt-0.5 text-[11px] text-gray-500">
          {days}d on site
        </div>
      )}

      {activeJobUsers.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1">
          <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          <div className="flex">
            {activeJobUsers.map((userId, key) => (
              <Avatar key={key} userId={userId} classes="w-6 h-6 rounded-full [&:not(:first-child)]:-ml-1.5" textClasses="text-[15px]" />
            ))}
          </div>
        </div>
      )}

      {(onMoveUp || onMoveDown) && (
        <div className="mt-1.5 flex items-center justify-end gap-x-1">
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp?.();
            }}
            disabled={!canMoveUp}
            className={iconButtonClasses}
            title="Move up"
          >
            <ChevronUpIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown?.();
            }}
            disabled={!canMoveDown}
            className={iconButtonClasses}
            title="Move down"
          >
            <ChevronDownIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
